import { ReactNode } from "react";
import {
  OrderStatus,
  CartItem,
  ModifierGroup,
  ModifierOptionInOrder,
} from "../../data/interfaces";
import {
  useAcceptOrderMutation,
  useFetchOrderDetailsQuery,
  useMakeOrderReadyMutation,
} from "../../store";
import { RiCheckLine, RiLoader4Line, RiRestaurant2Line } from "react-icons/ri";

const OrderDetails: React.FC<{ orderId: string }> = ({ orderId }) => {
  const { data: order, isFetching } = useFetchOrderDetailsQuery(orderId);
  const [acceptOrder] = useAcceptOrderMutation();
  const [makeOrderReady] = useMakeOrderReadyMutation();

  if (isFetching)
    return (
      <div className="flex justify-center items-center h-full text-gray-400 p-8">
        <RiLoader4Line className="animate-spin text-4xl" />
      </div>
    );

  if (!order)
    return (
      <div className="flex justify-center items-center h-full text-gray-400 p-8">
        <p>Commande introuvable</p>
      </div>
    );

  const renderedcartItems = order.cart.map((item: CartItem) => {
    const renderedModifierGroups = item.modifierGroups.map(
      (modG: ModifierGroup) => {
        const renderedModifierOptions = modG.modifierOptions.map(
          (modO: ModifierOptionInOrder) => {
            return (
              <div
                key={modO.modifierOptionId}
                className="flex justify-between text-gray-500 pl-2 py-1 text-sm"
              >
                <div className="flex gap-2 items-center">
                  <span className="bg-gray-100 text-gray-600 text-xs font-bold px-1.5 py-0.5 rounded">
                    x{modO.quantity}
                  </span>
                  <span>{modO.title}</span>
                </div>
                <div>{modO.price.text}</div>
              </div>
            );
          }
        );

        return (
          <div
            key={modG.modifierGroupId}
            className="flex flex-col gap-1 mt-2 pl-4 border-l-2 border-dashed border-gray-200"
          >
            <div className="text-sm font-semibold text-gray-600">
              {modG.title}
            </div>
            {renderedModifierOptions}
          </div>
        );
      }
    );

    return (
      <div
        key={item.itemId}
        className="flex flex-col gap-2 p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
      >
        <div className="flex justify-between items-start border-b border-gray-100 pb-2 mb-1">
          <div className="flex gap-3 items-center">
            <div className="bg-primary/10 text-primary rounded-md px-2 py-1 font-bold h-fit">
              {item.quantity}x
            </div>
            <div className="font-bold text-lg text-gray-800">{item.title}</div>
          </div>
          <div className="font-semibold text-gray-700">
            {item.price.baseUnitPrice.text}
          </div>
        </div>
        {renderedModifierGroups}
      </div>
    );
  });

  let renderedActionBtn: ReactNode = <></>;

  if (order.status === OrderStatus.PROCESSING) {
    renderedActionBtn = (
      <button
        className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-xl font-bold shadow-md transition-all flex items-center justify-center gap-2 text-base active:scale-95"
        onClick={() => {
          acceptOrder(order._id);
        }}
      >
        <RiCheckLine className="text-xl" />
        Accepter la commande
      </button>
    );
  } else if (order.status === OrderStatus.COOKING) {
    renderedActionBtn = (
      <button
        className="w-full bg-primary hover:bg-red-600 text-white py-3 px-6 rounded-xl font-bold shadow-md transition-all flex items-center justify-center gap-2 text-base active:scale-95"
        onClick={() => {
          makeOrderReady(order._id);
        }}
      >
        <RiRestaurant2Line className="text-xl" />
        Marquer comme prêt
      </button>
    );
  } else {
    renderedActionBtn = (
      <div className="text-center text-gray-500 font-medium py-4">
        Commande terminée
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">
            Commande #{order.displayId}
          </h2>
          <p className="text-gray-500 mt-0.5 flex items-center gap-2 text-xs">
            <span>{order.createdAt}</span>
          </p>
        </div>
        <div
          className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm ${
            order.status === OrderStatus.PROCESSING
              ? "bg-green-100 text-green-700 border border-green-200"
              : "bg-orange-100 text-orange-700 border border-orange-200"
          }`}
        >
          {order.status === OrderStatus.PROCESSING
            ? "Nouvelle commande"
            : "En préparation"}
        </div>
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 custom-scrollbar">
        {renderedcartItems}
      </div>

      {/* Footer / Actions */}
      <div className="p-4 bg-white border-t border-gray-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="flex justify-between items-center mb-4 px-1">
          <span className="text-gray-500 font-medium text-sm">
            Total de la commande
          </span>
          <span className="text-xl font-black text-gray-800">
            {order.paymentInfo.orderPrice.text}
          </span>
        </div>

        {renderedActionBtn}
      </div>
    </div>
  );
};

export default OrderDetails;
